import React, { useCallback, useEffect, useRef, useState } from 'react'
import dayjs from 'dayjs'
import { CircularProgress, makeStyles } from '@material-ui/core'
import { useExplorerConfigurations, useDeleteExplorerConfiguration } from '../../../api/coreData'
import { useAppContext, useHideSideMenuEffect } from '../../../redux/slices/appContext'
import ExplorerConfigToolbar from './ExplorerConfigToolbar'
import PortfolioExplorerGrid from './PortfolioExplorerGrid'
import { useExplorerConfig } from './hooks/useExplorerConfig'
import { usePortfolioExplorerQuery } from './hooks/usePortfolioExplorerQuery'
import { getLastViewId, setLastViewId, getRecentViews, pushRecentView } from './lib/viewStorage'

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
    background: 'var(--opal-surface-default, #fff)'
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    position: 'relative'
  },
  loading: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  empty: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--color-neutral-500, #888)',
    fontSize: 14
  },
  error: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    color: 'var(--color-danger-600, #c62828)',
    fontSize: 14
  },
  errorDetail: {
    fontSize: 12,
    color: 'var(--color-neutral-500, #888)'
  }
}))

export default function PortfolioExplorer () {
  const classes = useStyles()
  const { userId } = useAppContext()
  useHideSideMenuEffect()

  const {
    config,
    columnDefinitions,
    setGroupings,
    setColumns,
    setQuickDate,
    setDateRange,
    setFetchAllLevels,
    loadConfig,
    serializeConfig,
    isDirty,
    markClean
  } = useExplorerConfig()

  const [asOfDate, setAsOfDate] = useState(() => dayjs().subtract(1, 'day').format('YYYY-MM-DD'))

  const {
    rows,
    totalRow,
    isFetching,
    error,
    refetch,
    expandRow,
    expandToLevelLazy,
    expandAllChildrenLazy,
    lazyExpandState,
    cancelLazyExpand,
    hasClient,
    isReady
  } = usePortfolioExplorerQuery(config, asOfDate)

  const { data: savedConfigs, refetch: refetchConfigs } = useExplorerConfigurations()
  const deleteMutation = useDeleteExplorerConfiguration()

  const [activeConfigId, setActiveConfigId] = useState(null)
  const [activeViewName, setActiveViewName] = useState(null)
  const [recentViews, setRecentViews] = useState(() => getRecentViews(userId))
  const defaultLoadedRef = useRef(false)

  useEffect(() => {
    if (savedConfigs && !defaultLoadedRef.current) {
      defaultLoadedRef.current = true

      const lastId = getLastViewId(userId)
      const lastConfig = lastId ? savedConfigs.find(c => c.explorerConfigurationId === lastId) : null

      if (lastConfig) {
        loadConfig(lastConfig)
        setActiveConfigId(lastConfig.explorerConfigurationId)
        setActiveViewName(lastConfig.name)
        return
      }

      const defaultConfig = savedConfigs.find(c => c.isDefault)
      if (defaultConfig) {
        loadConfig(defaultConfig)
        setActiveConfigId(defaultConfig.explorerConfigurationId)
        setActiveViewName(defaultConfig.name)
        setLastViewId(userId, defaultConfig.explorerConfigurationId)
        pushRecentView(userId, defaultConfig.explorerConfigurationId, defaultConfig.name)
        setRecentViews(getRecentViews(userId))
      }
    }
  }, [savedConfigs, loadConfig, userId])

  const handleLoadConfig = useCallback((savedConfig) => {
    loadConfig(savedConfig)
    setActiveConfigId(savedConfig.explorerConfigurationId)
    setActiveViewName(savedConfig.name)
    setLastViewId(userId, savedConfig.explorerConfigurationId)
    pushRecentView(userId, savedConfig.explorerConfigurationId, savedConfig.name)
    setRecentViews(getRecentViews(userId))
  }, [loadConfig, userId])

  const handleSaveComplete = useCallback(({ explorerConfigurationId, name }) => {
    if (explorerConfigurationId != null) {
      setActiveConfigId(explorerConfigurationId)
      setLastViewId(userId, explorerConfigurationId)
      pushRecentView(userId, explorerConfigurationId, name)
      setRecentViews(getRecentViews(userId))
    }
    setActiveViewName(name)
    markClean()
  }, [markClean, userId])

  const handleDeleteConfig = useCallback((configId) => {
    deleteMutation.mutate(
      { explorerConfigurationId: configId },
      {
        onSuccess: () => {
          if (activeConfigId === configId) {
            setActiveConfigId(null)
            setActiveViewName(null)
            setLastViewId(userId, null)
          }
        }
      }
    )
  }, [deleteMutation, activeConfigId, userId])

  const canRun = hasClient && config.columns.length > 0 && isReady
  const hasResults = rows.length > 0 || error

  return (
    <div className={classes.root}>
      <ExplorerConfigToolbar
        config={config}
        setGroupings={setGroupings}
        setColumns={setColumns}
        setQuickDate={setQuickDate}
        setDateRange={setDateRange}
        setFetchAllLevels={setFetchAllLevels}
        isFetching={isFetching}
        onRefresh={refetch}
        canRun={canRun}
        savedConfigs={savedConfigs || []}
        activeConfigId={activeConfigId}
        activeViewName={activeViewName}
        isDirty={isDirty}
        onLoadConfig={handleLoadConfig}
        onDeleteConfig={handleDeleteConfig}
        onRefreshConfigs={refetchConfigs}
        serializeConfig={serializeConfig}
        onSaveComplete={handleSaveComplete}
        recentViews={recentViews}
        asOfDate={asOfDate}
        setAsOfDate={setAsOfDate}
      />

      <div className={classes.content}>
        {!hasClient && (
          <div className={classes.empty}>
            <p>Select a client to explore portfolio data.</p>
          </div>
        )}

        {hasClient && config.columns.length === 0 && (
          <div className={classes.empty}>
            <p>Select at least one column to display data.</p>
          </div>
        )}

        {canRun && isFetching && (
          <div className={classes.loading}>
            <CircularProgress size={32} />
          </div>
        )}

        {hasClient && error && !isFetching && (
          <div className={classes.error}>
            <p>Failed to load portfolio data.</p>
            <p className={classes.errorDetail}>{error.message || 'Unknown error'}</p>
          </div>
        )}

        {canRun && !isFetching && !hasResults && (
          <div className={classes.empty}>
            <p>Configure your view and click Run.</p>
          </div>
        )}

        {canRun && !isFetching && !error && rows.length > 0 && (
          <PortfolioExplorerGrid
            rows={rows}
            columnDefinitions={columnDefinitions}
            totalRow={totalRow}
            onExpandRow={expandRow}
            groupings={config.groupings}
            expandToLevelLazy={expandToLevelLazy}
            expandAllChildrenLazy={expandAllChildrenLazy}
            lazyExpandState={lazyExpandState}
            cancelLazyExpand={cancelLazyExpand}
            viewName={activeViewName}
            asOfDate={asOfDate}
          />
        )}
      </div>
    </div>
  )
}
